import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { ArrowLeft } from '@phosphor-icons/react';

/**
 * Public page that renders the Affiliate Program Agreement from /legal/affiliate-agreement.md
 * Required by the Affiliate Program for Rewardful signup compliance.
 */
export default function AffiliateAgreementPage() {
  const [markdown, setMarkdown] = useState('');
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch('/legal/affiliate-agreement.md')
      .then((r) => {
        if (!r.ok) throw new Error('not found');
        return r.text();
      })
      .then(setMarkdown)
      .catch(() => setError(true));
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="affiliate-agreement-page">
      <header className="border-b border-slate-200 bg-white sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="text-sm text-slate-600 hover:text-[#002FA7] inline-flex items-center gap-1"
            data-testid="back-home-link"
          >
            <ArrowLeft size={16} weight="bold" /> Back to SalesCrunchCRM
          </Link>
          <span className="text-xs text-slate-500">Affiliate Program Agreement</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4 text-sm" data-testid="affiliate-agreement-error">
            Unable to load the agreement. Please email{' '}
            <a className="underline" href="mailto:affiliates@migrationcrunch.com">
              affiliates@migrationcrunch.com
            </a>{' '}
            for a copy.
          </div>
        )}
        <article className="text-slate-700 leading-relaxed">
          <ReactMarkdown
            components={{
              h1: ({ children }) => (
                <h1 className="text-3xl font-black text-slate-900 mb-4 mt-2">{children}</h1>
              ),
              h2: ({ children }) => (
                <h2 className="text-xl font-bold text-slate-900 mt-8 mb-3">{children}</h2>
              ),
              h3: ({ children }) => (
                <h3 className="text-base font-bold text-slate-800 mt-5 mb-2">{children}</h3>
              ),
              p: ({ children }) => <p className="mb-4 text-sm">{children}</p>,
              ul: ({ children }) => <ul className="list-disc pl-6 mb-4 text-sm space-y-1">{children}</ul>,
              ol: ({ children }) => <ol className="list-decimal pl-6 mb-4 text-sm space-y-1">{children}</ol>,
              a: ({ href, children }) => (
                <a href={href} className="text-[#002FA7] hover:underline" target="_blank" rel="noopener noreferrer">
                  {children}
                </a>
              ),
              strong: ({ children }) => <strong className="font-semibold text-slate-900">{children}</strong>,
              hr: () => <hr className="my-6 border-slate-200" />,
              table: ({ children }) => (
                <div className="overflow-x-auto my-4">
                  <table className="w-full text-sm border border-slate-200">{children}</table>
                </div>
              ),
              th: ({ children }) => (
                <th className="bg-slate-50 border border-slate-200 px-3 py-2 text-left font-semibold text-slate-900">
                  {children}
                </th>
              ),
              td: ({ children }) => <td className="border border-slate-200 px-3 py-2">{children}</td>,
              em: ({ children }) => <em className="italic text-slate-600">{children}</em>,
            }}
          >
            {markdown}
          </ReactMarkdown>
        </article>
      </main>

      <footer className="border-t border-slate-200 bg-slate-50 px-6 py-6 text-xs text-slate-500 text-center">
        © 2026 SalesCrunchCRM. Questions?{' '}
        <a className="text-[#002FA7] hover:underline" href="mailto:affiliates@migrationcrunch.com">
          affiliates@migrationcrunch.com
        </a>
      </footer>
    </div>
  );
}
